import React, { useEffect, useState } from 'react';
import { get } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { fmtBRL, fmtDate } from '../lib/format';
import { Loader2 } from 'lucide-react';

const TYPE_LABELS = {
  RECEITA: { label: 'Receita Total', color: 'gold-text' },
  CUSTO: { label: 'Custos', color: 'red-text' },
  DESPESA: { label: 'Despesas', color: 'red-text' },
  IMPOSTO: { label: 'Impostos', color: 'text-amber-300' },
  INVESTIMENTO: { label: 'Investimentos', color: 'text-blue-400' },
};

/**
 * Drill-down de entries por tipo+competence+(opcional company_id).
 * Lista todas as linhas que compõem o KPI clicado.
 */
export default function EntriesDrillDown({ open, onClose, type, competence, companyId, title, filterCategoryId, filterCostCenterId, filterName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !type) return;
    setLoading(true);
    get('/entries', {
      type,
      competence,
      ...(companyId && companyId !== 'all' ? { company_id: companyId } : {}),
      ...(filterCategoryId ? { category_id: filterCategoryId } : {}),
      ...(filterCostCenterId ? { cost_center_id: filterCostCenterId } : {}),
    })
      .then((r) => {
        let arr = Array.isArray(r) ? r : [];
        // Se não há category/cost_center id (fallback description-based), filtra pelo nome/descrição
        if (!filterCategoryId && !filterCostCenterId && filterName) {
          const needle = filterName.toLowerCase();
          arr = arr.filter((e) => (e.description || '').toLowerCase().includes(needle));
        }
        setEntries(arr);
      })
      .finally(() => setLoading(false));
  }, [open, type, competence, companyId, filterCategoryId, filterCostCenterId, filterName]);

  const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const meta = TYPE_LABELS[type] || { label: type, color: 'text-zinc-300' };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="dialog-entries-drilldown">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between">
            <span>{title || `Detalhamento — ${meta.label}`}</span>
            <span className={`font-mono text-base ${meta.color}`}>{fmtBRL(total)}</span>
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            {entries.length} lançamento{entries.length === 1 ? '' : 's'} na competência {competence}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-400" size={20} />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm">
            Nenhum lançamento encontrado nesta competência.
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-800 rounded-md">
            <table className="w-full ceap-table" data-testid="drilldown-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  {type === 'RECEITA' && <th>Município</th>}
                  <th>Tipo</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="font-mono text-xs text-zinc-400">{fmtDate(e.date)}</td>
                    <td className="text-zinc-100">{e.description}</td>
                    {type === 'RECEITA' && (
                      <td className="text-zinc-300 text-xs">
                        {e.municipality || <span className="text-zinc-600">—</span>}
                      </td>
                    )}
                    <td className="text-xs text-zinc-500">{e.type}</td>
                    <td className="text-center">
                      <span className={`pill text-[10px] ${e.status === 'PAGO' || e.status === 'CONCILIADO' ? 'pill-success' : 'pill-warning'}`}>{e.status}</span>
                    </td>
                    <td className={`text-right font-mono ${meta.color}`}>{fmtBRL(e.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-900/40 border-t-2 border-amber-700/40">
                  <td colSpan={type === 'RECEITA' ? 5 : 4} className="text-right text-zinc-100 font-medium">TOTAL</td>
                  <td className={`text-right font-mono text-base font-bold ${meta.color}`}>{fmtBRL(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
